import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import '../../styles/route.css';

/**
 * "Who's signing up?" — the role chooser that fronts registration.
 *
 * Each role has a genuinely different setup (customer self-serve, driver
 * onboarding review, company OTP + claim), so asking once here is cheaper than
 * making one form apologise for three journeys. This screen only *selects* the
 * role — every card hands off to the flow that already exists.
 */

interface Role {
  /** Single letter, per the design's icon tiles. */
  initial: string;
  title: string;
  copy: string;
  action: string;
  to: string;
  /** Customer is the primary path — the only Signal Yellow on this viewport. */
  primary?: boolean;
}

const ROLES: Role[] = [
  {
    initial: 'U',
    title: 'Customer',
    copy: 'Book trucks and manage deliveries. Sign up in minutes with email or Google.',
    action: 'Sign up as a customer',
    to: '/register?role=customer',
    primary: true,
  },
  {
    initial: 'D',
    title: 'Driver',
    copy: 'Complete onboarding and receive assignments. Approved operators and fleet owners.',
    action: 'Start driver onboarding',
    to: '/register?role=driver',
  },
  {
    initial: 'C',
    title: 'Company owner',
    copy: 'Claim your company and manage driver visibility. Email verification via OTP.',
    action: 'Register a company',
    to: '/register?role=company',
  },
];

const RoleChooser: React.FC = () => {
  return (
    <div className="landing-shell">
      <Header />
      <main>
        <section className="sw-section">
          <div className="container">
            <div className="sw-choice-head">
              <span className="sw-eyebrow">Create your account</span>
              <h1>Who&apos;s signing up?</h1>
              <p>Pick the path that matches you — each role has its own setup.</p>
            </div>

            <div className="sw-choice-grid">
              {ROLES.map((role) => (
                <div className="sw-audience-card" key={role.title}>
                  <span className="sw-choice-icon" aria-hidden="true">
                    {role.initial}
                  </span>
                  <h2 className="sw-choice-title">{role.title}</h2>
                  <p className="sw-choice-copy">{role.copy}</p>
                  <Link
                    className={role.primary ? 'sw-btn-primary' : 'sw-btn-secondary'}
                    to={role.to}
                  >
                    {role.action}
                  </Link>
                </div>
              ))}
            </div>

            <p className="sw-choice-footnote">
              Already have an account? <Link to="/login">Log in</Link>
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default RoleChooser;
